import React, { useMemo } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  evaluateSlot,
  scheduleOems,
  slotLabel,
  SAME_OEM_GAP_MINUTES,
  type SlotContext
} from '../../utils/scheduleSlots';
import { getNextRun } from '../../utils/cronNextRun';
import type { PipelineSchedule } from '../../services/pipelineOrchestrator';

interface ScheduleSequencePreviewProps {
  /** Every schedule, so collisions across tenants are visible. */
  existing: PipelineSchedule[];
  /** OEM prefixes the new schedule will crawl. */
  oems: string[];
  daysOfWeek: number[];
  hour: number;
  minute: number;
  scheduleZone: string;
  dealerZone: string;
  /** Excluded from the comparison when editing, so a schedule can't clash with itself. */
  editingScheduleId?: string | undefined;
}

const MINUTES_PER_DAY = 24 * 60;

/** Axis labels every three hours. */
const TICKS = [0, 3, 6, 9, 12, 15, 18, 21];

interface Marker {
  id: string;
  pct: number;
  sameOem: boolean;
  label: string;
}

/** Where an existing schedule sits on the axis, in the comparison zone. */
function markerFor(
  schedule: PipelineSchedule,
  zone: string,
  wanted: Set<string>,
  now: Date
): Marker | null {
  const run = getNextRun(schedule.cron_expression, schedule.time_zone, now);
  if (!run) return null;

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(run.instant);
  const h = Number(parts.find(p => p.type === 'hour')?.value);
  const m = Number(parts.find(p => p.type === 'minute')?.value);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;

  const minuteOfDay = (h % 24) * 60 + m;
  const oems = scheduleOems(schedule);
  const sameOem = oems.some(o => wanted.has(o));

  return {
    id: schedule.id,
    pct: (minuteOfDay / MINUTES_PER_DAY) * 100,
    sameOem,
    label: `${schedule.schedule_name}\n${slotLabel({
      hour: Math.floor(minuteOfDay / 60),
      minute: minuteOfDay % 60
    })}${oems.length > 0 ? ` · ${oems.join(', ')}` : ''}`
  };
}

type VerdictTone = 'good' | 'warn' | 'bad';

const VERDICT_CLASS: Record<VerdictTone, string> = {
  bad: 'text-red-600 dark:text-red-500',
  warn: 'text-amber-600 dark:text-amber-500',
  good: 'text-muted-foreground'
};

/**
 * The one-line answer to "is this a sensible time".
 *
 * Ordered by severity: a same-brand clash is the thing that actually breaks a
 * crawl, so it outranks a merely awkward hour.
 */
function verdictFor(
  slot: ReturnType<typeof evaluateSlot>,
  sameOemCount: number
): { tone: VerdictTone; text: string } {
  if (slot.conflictingOem) {
    return {
      tone: 'bad',
      text:
        `${slot.conflictingOem.toUpperCase()} also runs ` +
        `${slot.nearestOemGapMinutes} min from this time — under the ` +
        `${SAME_OEM_GAP_MINUTES} min gap, so they may block each other.`
    };
  }
  if (!slot.offHours) {
    return { tone: 'bad', text: 'The dealership is open at this hour.' };
  }
  if (!slot.finishesBeforeOpen) {
    return { tone: 'warn', text: 'This may still be running when the dealership opens.' };
  }
  return {
    tone: 'good',
    text:
      sameOemCount === 0
        ? 'Clear — nothing else crawls these brands.'
        : `Clear — nearest same-brand run is ${slot.nearestOemGapMinutes} min away.`
  };
}

/**
 * Where this schedule lands relative to everything else already running.
 *
 * The question an operator actually has is "am I about to crash into another
 * crawl of the same brand" — so schedules sharing an OEM are drawn prominently
 * and everything else is muted context. Positions come from real instants, so
 * schedules stored in different timezones line up honestly.
 */
// eslint-disable-next-line max-lines-per-function
export const ScheduleSequencePreview: React.FC<ScheduleSequencePreviewProps> = ({
  existing,
  oems,
  daysOfWeek,
  hour,
  minute,
  scheduleZone,
  dealerZone,
  editingScheduleId
}) => {
  const now = useMemo(() => new Date(), []);

  const others = useMemo(
    () => existing.filter(s => s.is_enabled && s.id !== editingScheduleId),
    [existing, editingScheduleId]
  );

  const ctx: SlotContext = useMemo(
    () => ({ existing: others, oems, daysOfWeek, scheduleZone, dealerZone }),
    [others, oems, daysOfWeek, scheduleZone, dealerZone]
  );

  const slot = useMemo(() => evaluateSlot(hour, minute, ctx, now), [hour, minute, ctx, now]);

  const markers = useMemo(() => {
    const wanted = new Set(oems);
    return others
      .map(s => markerFor(s, scheduleZone, wanted, now))
      .filter((m): m is Marker => m !== null);
  }, [others, oems, scheduleZone, now]);

  const minePct = ((hour * 60 + minute) / MINUTES_PER_DAY) * 100;
  const sameOemCount = markers.filter(m => m.sameOem).length;

  if (oems.length === 0) {
    return (
      <p className='rounded-md border bg-muted/30 px-2.5 py-2 text-xs text-muted-foreground'>
        Pick zones to see how this schedule sequences against the others.
      </p>
    );
  }

  const verdict = verdictFor(slot, sameOemCount);
  const verdictClass = VERDICT_CLASS[verdict.tone];

  return (
    <div className='space-y-1.5 rounded-md border bg-muted/20 px-2.5 py-2'>
      <div className='flex items-baseline justify-between'>
        <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>
          How this fits in
        </p>
        <span className='text-[10px] text-muted-foreground'>
          {markers.length} other schedule{markers.length === 1 ? '' : 's'}
          {sameOemCount > 0 && ` · ${sameOemCount} same brand`}
        </span>
      </div>

      <div className='relative h-9'>
        {/* Business-hours band, dealer-local, so "inside the shading" reads as
            "staff are working". Drawn behind the markers. */}
        <BusinessHoursBand scheduleZone={scheduleZone} dealerZone={dealerZone} now={now} />

        <div className='absolute inset-x-0 top-4 h-px bg-border' />

        {markers.map(m => (
          <span
            key={m.id}
            title={m.label}
            className={
              'absolute top-2 w-px -translate-x-1/2 ' +
              (m.sameOem ? 'h-5 bg-primary' : 'h-3 bg-muted-foreground/40')
            }
            style={{ left: `${m.pct}%` }}
          />
        ))}

        {/* This schedule. Triangle so it never reads as one of the others. */}
        <span
          className='absolute top-0 -translate-x-1/2 text-[10px] leading-none'
          style={{ left: `${minePct}%` }}
          title={`This schedule · ${slotLabel({ hour, minute })}`}
        >
          <span className={verdict.tone === 'bad' ? 'text-red-500' : 'text-foreground'}>▼</span>
        </span>

        <div className='absolute inset-x-0 bottom-0 flex justify-between'>
          {TICKS.map(t => (
            <span key={t} className='text-[9px] text-muted-foreground'>
              {t === 0 ? '12a' : t < 12 ? `${t}a` : t === 12 ? '12p' : `${t - 12}p`}
            </span>
          ))}
        </div>
      </div>

      <p className={`flex items-start gap-1 text-[11px] ${verdictClass}`}>
        {verdict.tone === 'good' ? (
          <CheckCircle2 className='mt-px h-3 w-3 shrink-0' />
        ) : (
          <AlertTriangle className='mt-px h-3 w-3 shrink-0' />
        )}
        {verdict.text}
      </p>
    </div>
  );
};

/**
 * Shades the hours the dealership is open, positioned on the schedule zone's
 * axis. Wraps across midnight when the zones differ enough, hence two spans.
 */
const BusinessHoursBand: React.FC<{
  scheduleZone: string;
  dealerZone: string;
  now: Date;
}> = ({ scheduleZone, dealerZone, now }) => {
  const { startPct, endPct } = useMemo(() => {
    // Probe each half hour of the schedule-zone day and ask whether the dealer
    // is open then. 48 probes, and it reuses the same off-hours rule the
    // suggestions run on rather than duplicating the offset maths here.
    const probeCtx: SlotContext = {
      existing: [],
      oems: [],
      daysOfWeek: [0],
      scheduleZone,
      dealerZone
    };

    const open: number[] = [];
    for (let m = 0; m < MINUTES_PER_DAY; m += 30) {
      if (!evaluateSlot(Math.floor(m / 60), m % 60, probeCtx, now).offHours) open.push(m);
    }

    if (open.length === 0) return { startPct: 0, endPct: 0 };
    const first = open[0] ?? 0;
    const last = open[open.length - 1] ?? 0;
    return {
      startPct: (first / MINUTES_PER_DAY) * 100,
      endPct: ((last + 30) / MINUTES_PER_DAY) * 100
    };
  }, [scheduleZone, dealerZone, now]);

  if (endPct <= startPct) return null;

  return (
    <span
      className='absolute top-2 h-5 bg-amber-500/10'
      style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }}
      title='Dealership is open'
    />
  );
};
